import PropTypes from 'prop-types';
import WaterfallChart from '@/components/charts/WaterfallChart';
import { parseLCAForWaterfall } from './waterfallUtils';

/**
 * LCA result card.
 *
 * compact=true  → chat bubble: hero + scope grid (no chart)
 * compact=false → workspace:   hero + scope grid + WaterfallChart
 */

function ScopeGrid({ scope1, scope2, scope3 }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      {[
        { label: 'Scope 1', value: scope1 },
        { label: 'Scope 2', value: scope2 },
        { label: 'Scope 3', value: scope3 },
      ].map(({ label, value }) => (
        <div key={label}>
          <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">{label}</div>
          <div className="text-sm font-semibold text-slate-700 tabular-nums">{value.toFixed(2)}</div>
        </div>
      ))}
    </div>
  );
}

ScopeGrid.propTypes = {
  scope1: PropTypes.number.isRequired,
  scope2: PropTypes.number.isRequired,
  scope3: PropTypes.number.isRequired,
};

function LCAResultCard({ data, compact = true }) {
  const { scope1, scope2, scope3, total, unit, breakdown } = data;

  const card = (
    <div className={`bg-white border border-stone-200 rounded-md p-4 space-y-3 ${compact ? 'w-full max-w-[85%]' : 'w-full'}`}>
      {/* Hero */}
      <div>
        <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Carbon Intensity</div>
        <div className="flex items-baseline gap-2">
          <span className={`font-bold text-slate-900 ${compact ? 'text-2xl' : 'text-4xl'}`}>{total.toFixed(2)}</span>
          <span className={`text-slate-400 ${compact ? 'text-sm' : 'text-base'}`}>{unit}</span>
        </div>
      </div>

      {/* Scope breakdown */}
      <ScopeGrid scope1={scope1} scope2={scope2} scope3={scope3} />

      {/* Waterfall (workspace only) */}
      {!compact && breakdown?.length > 0 && (
        <div className="pt-2 border-t border-stone-100">
          <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Breakdown</div>
          <WaterfallChart
            data={parseLCAForWaterfall(breakdown)}
            label="Carbon Intensity"
            yAxisLabel={unit}
          />
        </div>
      )}
    </div>
  );

  if (!compact) return card;
  return (
    <div className="flex gap-2.5 items-start">
      <div className="w-6 shrink-0" />
      {card}
    </div>
  );
}

LCAResultCard.propTypes = {
  compact: PropTypes.bool,
  data: PropTypes.shape({
    scope1: PropTypes.number.isRequired,
    scope2: PropTypes.number.isRequired,
    scope3: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    unit: PropTypes.string.isRequired,
    breakdown: PropTypes.array,
  }).isRequired,
};

export default LCAResultCard;
